<template>
  <div>
    <section-page
      v-show="activeCallScripts.length"
      title="Default Call Script"
      description="Set a default call script that will allow you to open inside the lead modal."
      is-short
    >
      <form-field-select
        label="Call Scripts"
        v-model="defaultCallScript"
      >
        <option value="0">None</option>
        <option
          v-for="callscript in activeCallScripts"
          :key="callscript.id"
          :value="callscript.id"
        >{{ callscript.name }}</option>
      </form-field-select>
    </section-page>

    <section-page title="Lead Call Scripts">
      <template v-slot:right>
        <filter-link link-name="store" :link-params="{tab: 'callScripts'}">Get call scripts from Store</filter-link>
        <filter-button-add :disabled="creatingContactCallScript || creatingLeadCallScript" :loading="creatingLeadCallScript" @click="create(ENUMS.CallScriptTypeEnum.Lead.value)">
          Lead Call Script
        </filter-button-add>
      </template>

      <app-loading-section
        :loading="loading"
        :count="leadCallScripts.length"
        heading="You don't have any call scripts"
      >
        <table-default
          :table-data="leadCallScripts"
          default-sort="name"
        >
          <b-table-column label="Name" field="name" sortable v-slot="props">
            {{ props.row.name }}
          </b-table-column>

          <b-table-column label="Status" field="status" width="100" sortable v-slot="props">
            {{ props.row.status | newEnumLabel(ENUMS.CallScriptStatusEnum) }}
          </b-table-column>

          <b-table-column label="# Steps" field="number_of_steps" numeric width="100" sortable v-slot="props">
            {{ props.row.number_of_steps }}
          </b-table-column>

          <b-table-column label="URL" field="url" v-slot="props">
            <a :href="props.row.url" target="_blank">
              {{ props.row.url }}
            </a>
          </b-table-column>

          <b-table-column cell-class="action_cell" width="200" v-slot="props">
            <button-link @click="edit(props.row)">Manage</button-link>
            <button-link :loading="isDuplicating" @click="duplicate(props.row.id)">Duplicate</button-link>
          </b-table-column>
        </table-default>
      </app-loading-section>
    </section-page>



    <section-page title="Contact Call Scripts">
      <template v-slot:right>
        <filter-button-add  :disabled="creatingContactCallScript || creatingLeadCallScript" :loading="creatingLeadCallScript" @click="create(ENUMS.CallScriptTypeEnum.Contact.value)">
          Contact Call Script
        </filter-button-add>
      </template>

      <app-loading-section
        :loading="loading"
        :count="ContactCallScripts.length"
        heading="You don't have any call scripts"
      >
        <table-default
          :table-data="ContactCallScripts"
          default-sort="name"
        >
          <b-table-column label="Name" field="name" sortable v-slot="props">
            {{ props.row.name }}
          </b-table-column>

          <b-table-column label="Status" field="status" width="100" sortable v-slot="props">
            {{ props.row.status | newEnumLabel(ENUMS.CallScriptStatusEnum) }}
          </b-table-column>

          <b-table-column label="# Steps" field="number_of_steps" numeric width="100" sortable v-slot="props">
            {{ props.row.number_of_steps }}
          </b-table-column>

          <b-table-column label="URL" field="url" v-slot="props">
            <a :href="props.row.url" target="_blank">
              {{ props.row.url }}
            </a>
          </b-table-column>

          <b-table-column cell-class="action_cell" width="200" v-slot="props">
            <button-link @click="edit(props.row)">Manage</button-link>
            <button-link :loading="isDuplicating" @click="duplicate(props.row.id)">Duplicate</button-link>
          </b-table-column>
        </table-default>
      </app-loading-section>
    </section-page>






    <modal-container>
      <modal-form-edit
        ref="callScriptEditModal"
        title="Call Script"
        form-component="call-script-form"
      >
        <template v-slot:underForm>
          <call-script-identifier-form :call-script="callScript"></call-script-identifier-form>
        </template>

        <call-script-steps :call-script-type="callScript.type" :call-script-id="callScript.id"></call-script-steps>
      </modal-form-edit>
    </modal-container>
  </div>
</template>

<script>
import CallScriptIdentifierForm from "../../forms/CallScriptIdentifierForm";
import CallScriptSteps from "./components/CallScriptSteps";
import {ENUMS} from "../../services/enums";

export default {
  name: "CallScriptsTab",
  components: {
    CallScriptIdentifierForm,
    CallScriptSteps,
  },
  data() {
    return {
      callScript: {},
      creatingLeadCallScript: false,
      creatingContactCallScript: false,
      loading: false,
      isDuplicating: false
    }
  },
  computed: {
    ENUMS() {
      return ENUMS;
    },
    callScripts() {
      return this.$store.getters.callScripts;
    },
    leadCallScripts() {
      return this.callScripts.filter(cs => cs.type === ENUMS.CallScriptTypeEnum.Lead.value);
    },
    ContactCallScripts() {
      return this.callScripts.filter(cs => cs.type  === ENUMS.CallScriptTypeEnum.Contact.value);
    },
    activeCallScripts() {
      return this.callScripts.filter(cs => cs.status === ENUMS.CallScriptStatusEnum.Active.value);
    },
    defaultCallScript: {
      get() {
        const callScript = this.callScripts.find(cs => cs.is_default);
        return (callScript) ? callScript.id : 0;
      },
      set(id) {
        this.$api.callScripts.setDefault(id)
          .then(res => {
            this.$buefy.toast.open({
              message: res.data,
              type: 'is-success'
            });
          });
      }
    },
  },
  methods: {
    create(CallScriptType) {
      if (CallScriptType === ENUMS.CallScriptTypeEnum.Lead.value) {
        this.creatingLeadCallScript = true;
      } else {
        this.creatingContactCallScript = true;
      }

      const formData = {
        market_id: this.$store.getters.market.id,
        type: CallScriptType,
      };

      this.$api.callScripts.create(formData)
        .then(res => {
          this.$buefy.toast.open({
            message: "Call script was created",
            type: 'is-success'
          });

          this.edit(res.data);
        })
        .finally(() => {
          this.creatingLeadCallScript = false;
          this.creatingContactCallScript = false;
        });
    },
    edit(callScript) {
      this.callScript = callScript;
      this.$refs.callScriptEditModal.open(callScript);
    },
    duplicate(callScriptId) {
      this.isDuplicating = true;
      this.$api.callScripts.clone(callScriptId)
        .then(res => {
          this.$buefy.toast.open({
            message: "Copied call script",
            type: 'is-success'
          });

          this.edit(res.data);
        })
        .finally(() => this.isDuplicating = false);
    }
  }
}
</script>
